import { useEffect, useCallback } from 'react';
import { getSocket } from '@/lib/socket';
import { useRoomStore } from '@/stores/roomStore';
import { usePlayerStore } from '@/stores/playerStore';
import { useUserStore } from '@/stores/userStore';
import type { SyncCommand } from '@/types';

export const useSocket = (roomId: string) => {
  const socket = getSocket();
  const {
    setRoom,
    setParticipants,
    setPlaylist,
    setCurrentIndex,
    setMyRole,
    setConnected,
    addParticipant,
    removeParticipant,
    updateParticipantRole,
  } = useRoomStore();

  const { setVideoId, setPosition, setIsPlaying } = usePlayerStore();
  const { userSecret } = useUserStore();

  const joinRoom = useCallback(() => {
    socket.emit('room:join', { roomId, userSecret }, (response: { success: boolean; error?: string }) => {
      if (!response.success) {
        console.error('Failed to join room:', response.error);
        // Handle different error cases
        if (response.error === 'Room not found') {
          alert('The room no longer exists. Redirecting to home...');
          useRoomStore.getState().reset();
          usePlayerStore.getState().reset();
          window.location.href = '/';
        } else if (response.error === 'User not registered') {
          alert('Please register first. Redirecting...');
          window.location.href = '/register';
        } else if (response.error === 'Room is closed') {
          alert('This room is closed. Only the owner can reopen it.');
          window.location.href = '/';
        }
      }
    });
  }, [socket, roomId, userSecret]);

  useEffect(() => {
    // Connection handlers
    socket.on('connect', () => {
      console.log('Socket connected');
      setConnected(true);
      joinRoom();
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      setConnected(false);
    });

    // Room event handlers
    socket.on('room:joined', (data) => {
      console.log('Joined room:', data);
      setRoom(data.room);
      setParticipants(data.participants);
      setPlaylist(data.playlist);
      setCurrentIndex(data.currentIndex);
      setMyRole(data.myRole);

      // If there's a video currently playing/paused, update state
      // The useYouTubePlayer hook will react to videoId changes and load the video
      if (data.currentState.videoId) {
        console.log('Syncing to current video:', data.currentState);
        setVideoId(data.currentState.videoId);
        setPosition(data.currentState.position);
        setIsPlaying(data.currentState.isPlaying);
      }
    });

    socket.on('room:user-joined', (data) => {
      console.log('User joined:', data);
      addParticipant(data);
    });

    socket.on('room:user-left', (data) => {
      console.log('User left:', data.userId);
      removeParticipant(data.userId);
    });

    socket.on('room:closed', (data) => {
      console.log('Room closed:', data.reason);
      alert(`Room closed: ${data.reason}`);
      window.location.href = '/';
    });

    // Sync event handlers
    socket.on('sync:command', (data: SyncCommand) => {
      console.log('Sync command:', data);
      // Note: Don't set videoId here! The useYouTubePlayer hook will handle
      // loading the video with the correct position. Setting videoId here would
      // trigger the videoId change effect before the sync command handler can
      // set the loadedFromSyncCommand flag, causing race conditions.
      // This will be handled by the YouTube player hook
    });

    socket.on('sync:state-update', (data) => {
      console.log('State update:', data);
      if (data.videoId) setVideoId(data.videoId);
      setPosition(data.position);
      setIsPlaying(data.isPlaying);
    });

    // Playlist event handlers
    socket.on('playlist:updated', (data) => {
      console.log('Playlist updated:', data);
      setPlaylist(data.playlist);
      setCurrentIndex(data.currentIndex);
    });

    socket.on('playlist:track-changed', (data) => {
      console.log('Track changed:', data);
      setCurrentIndex(data.index);
      // Note: Don't set videoId here! The sync:command handler will load the video
      // with the correct position. Setting videoId here would trigger the videoId
      // change effect with the old position from the store, causing the video to
      // start at the wrong position when replaying a previously played track.
    });

    // Role event handlers
    socket.on('role:updated', (data) => {
      console.log('Role updated:', data.role);
      setMyRole(data.role);
    });

    socket.on('participants:updated', (data) => {
      console.log('Participants updated:', data);
      setParticipants(data.participants);
    });

    socket.on('dj:granted', (data) => {
      console.log('DJ granted:', data);
      updateParticipantRole(data.userId, 'dj');
    });

    socket.on('dj:revoked', (data) => {
      console.log('DJ revoked:', data);
      updateParticipantRole(data.userId, 'listener');
    });

    // Error handler
    socket.on('error', (data) => {
      console.error('Socket error:', data);
      alert(`Error: ${data.message}`);
    });

    // Join room on mount
    if (socket.connected) {
      joinRoom();
    }

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('room:joined');
      socket.off('room:user-joined');
      socket.off('room:user-left');
      socket.off('room:closed');
      socket.off('sync:command');
      socket.off('sync:state-update');
      socket.off('playlist:updated');
      socket.off('playlist:track-changed');
      socket.off('role:updated');
      socket.off('participants:updated');
      socket.off('dj:granted');
      socket.off('dj:revoked');
      socket.off('error');
    };
  }, [socket, roomId, userSecret, joinRoom, setRoom, setParticipants, setPlaylist, setCurrentIndex, setMyRole, setConnected, addParticipant, removeParticipant, updateParticipantRole, setVideoId, setPosition, setIsPlaying]);

  return { socket };
};
